import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import * as informativePageActions from '../informative-page/informative-page.actions';
import * as interventionActions from './intervention.actions';
import * as interventionModuleActions from '../intervention-content-item/intervention-content-item.actions';
import { InterventionService } from '../../services/intervention/intervention.service';
import { DashboardSnakeCasePipe } from 'src/app/core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { Intervention } from 'src/app/models/intervention';
import { StudyType } from '../../../models/study-type';
import { AppState } from '../../../state';
import { selectSelectedStudy } from '../study/study.selectors';
import { selectInterventions } from './intervention.selectors';
import { noopAction } from '../study/study.actions';

export function transformInterventionFromApi(intervention): Intervention {
  return {
    ...intervention,
    studyId: Number(intervention.studyId),
  };
}

@Injectable()
export class InterventionEffects {
  saveIntervention$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.saveIntervention),
      switchMap(({ intervention }) => {
        const transformedInterventionApi = this.dashboardSnakeCasePipe.keysToSnakeCase(intervention);
        return this.interventionService.saveIntervention(transformedInterventionApi).pipe(
          map((newIntervention) => {
            const newInterventionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newIntervention);
            return interventionActions.saveInterventionSuccess({
              newIntervention: transformInterventionFromApi(newInterventionCamelCase),
            });
          }),
          catchError((err: HttpErrorResponse) => of(interventionActions.saveInterventionError({ err })))
        );
      })
    )
  );

  saveInterventionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.saveInterventionSuccess),
      withLatestFrom(this.store.select(selectSelectedStudy), this.store.select(selectInterventions)),
      switchMap(([{ newIntervention }, selectedStudy, interventions]) => {
        const interventionModules = newIntervention.modulePivots;
        const interventionInformativePages = newIntervention.pagePivots;
        this.toastrService.success(`Intervention saved`);

        const saveInterventionSuccessActions = [];

        saveInterventionSuccessActions.push(
          informativePageActions.getInformativePages({
            studyId: newIntervention.studyId,
          }),
          interventionModuleActions.getInterventionContentItemsSuccess({
            interventionContentItems: interventionModules.concat(interventionInformativePages),
          })
        );

        if (newIntervention.isDefault) {
          const nonDefaultInterventions = interventions.filter(
            (intervention) => intervention.id !== newIntervention.id
          );
          saveInterventionSuccessActions.push(
            interventionActions.updateNonDefaultInterventions({ nonDefaultInterventions })
          );
        }

        return saveInterventionSuccessActions;
      })
    )
  );

  saveInterventionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(interventionActions.saveInterventionError),
        withLatestFrom(this.store.select(selectSelectedStudy)),
        tap(([{ err }, selectedStudy]) => this.toastrService.error(err.error.error, `Unable to save intervention`))
      ),
    { dispatch: false }
  );

  updateIntervention$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.updateIntervention),
      switchMap(({ intervention }) => {
        const transformedInterventionApi = this.dashboardSnakeCasePipe.keysToSnakeCase(intervention);
        return this.interventionService.updateIntervention(transformedInterventionApi).pipe(
          map((newIntervention) => {
            const newInterventionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newIntervention);
            return interventionActions.updateInterventionSuccess({
              updatedIntervention: transformInterventionFromApi(newInterventionCamelCase),
            });
          }),
          catchError((err: HttpErrorResponse) => of(interventionActions.updateInterventionError({ err })))
        );
      })
    )
  );

  updateInterventionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.updateInterventionSuccess),
      withLatestFrom(this.store.select(selectSelectedStudy), this.store.select(selectInterventions)),
      map(([{ updatedIntervention }, selectedStudy, interventions]) => {
        this.toastrService.success('Intervention saved');

        if (updatedIntervention.isDefault) {
          const nonDefaultInterventions = interventions.filter(
            (intervention) => intervention.id !== updatedIntervention.id
          );
          return interventionActions.updateNonDefaultInterventions({ nonDefaultInterventions });
        }

        return noopAction();
      })
    )
  );

  updateInterventionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(interventionActions.updateInterventionError),
        withLatestFrom(this.store.select(selectSelectedStudy)),
        tap(([{ err }, selectedStudy]) => this.toastrService.error(`Unable to save intervention`))
      ),
    { dispatch: false }
  );

  deleteIntervention$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.deleteIntervention),
      switchMap(({ intervention }) => {
        const transformedInterventionApi = this.dashboardSnakeCasePipe.keysToSnakeCase(intervention);
        return this.interventionService.deleteIntervention(transformedInterventionApi).pipe(
          map(() =>
            interventionActions.deleteInterventionSuccess({
              deletedIntervention: intervention,
            })
          ),
          catchError((err: HttpErrorResponse) => of(interventionActions.deleteInterventionError({ err })))
        );
      })
    )
  );

  deleteInterventionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(interventionActions.deleteInterventionSuccess),
        withLatestFrom(this.store.select(selectSelectedStudy)),
        tap(([newIntervention, selectedStudy]) => this.toastrService.success(`Intervention deleted`))
      ),
    { dispatch: false }
  );

  deleteInterventionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(interventionActions.deleteInterventionError),
        withLatestFrom(this.store.select(selectSelectedStudy)),
        tap(([{ err }, selectedStudy]) => this.toastrService.error(`Unable to delete intervention`))
      ),
    { dispatch: false }
  );

  duplicateIntervention$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.duplicateIntervention),
      switchMap(({ intervention }) => {
        const transformedInterventionApi = this.dashboardSnakeCasePipe.keysToSnakeCase(intervention);
        return this.interventionService.duplicateIntervention(transformedInterventionApi).pipe(
          map((newIntervention) => {
            const newInterventionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newIntervention);
            return interventionActions.duplicateInterventionSuccess({
              duplicatedIntervention: transformInterventionFromApi(newInterventionCamelCase),
            });
          }),
          catchError((err: HttpErrorResponse) => of(interventionActions.duplicateInterventionError({ err })))
        );
      })
    )
  );

  duplicateInterventionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(interventionActions.duplicateInterventionSuccess),
        withLatestFrom(this.store.select(selectSelectedStudy)),
        tap(([newIntervention, selectedStudy]) => this.toastrService.success(`Intervention duplicated`))
      ),
    { dispatch: false }
  );

  duplicateInterventionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(interventionActions.duplicateInterventionError),
        withLatestFrom(this.store.select(selectSelectedStudy)),
        tap(([{ err }, selectedStudy]) => this.toastrService.error(`Unable to duplicate intervention`))
      ),
    { dispatch: false }
  );

  finishSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.finishSession),
      switchMap(({ intervention }) => {
        const transformedInterventionApi = this.dashboardSnakeCasePipe.keysToSnakeCase(intervention);
        return this.interventionService.finishSession(transformedInterventionApi).pipe(
          map((newIntervention) => {
            const newInterventionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newIntervention);
            return interventionActions.finishSessionSuccess({
              intervention: transformInterventionFromApi(newInterventionCamelCase),
            });
          }),
          catchError((err: HttpErrorResponse) => of(interventionActions.finishSessionError({ err })))
        );
      })
    )
  );

  finishSessionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.finishSessionSuccess),
      map(({ intervention }) =>
        interventionActions.updateInterventionSuccess({
          updatedIntervention: intervention,
        })
      )
    )
  );

  finishSessionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(interventionActions.finishSessionError),
        withLatestFrom(this.store.select(selectSelectedStudy)),
        tap(([{ err }, selectedStudy]) => this.toastrService.error(`Unable to save intervention}`))
      ),
    { dispatch: false }
  );

  getIntervention$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.getIntervention),
      switchMap(({ studyId, interventionId }) => {
        return this.interventionService.getIntervention(studyId, interventionId).pipe(
          map((newIntervention) => {
            const newInterventionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newIntervention);
            return interventionActions.getInterventionSuccess({
              intervention: transformInterventionFromApi(newInterventionCamelCase),
            });
          }),
          catchError((err: HttpErrorResponse) => of(interventionActions.getInterventionError({ err })))
        );
      })
    )
  );

  getInterventionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.getInterventionSuccess),
      switchMap(({ intervention }) => {
        const interventionModules = intervention.modulePivots;
        const interventionInformativePages = intervention.pagePivots;
        return [
          informativePageActions.getInformativePages({
            studyId: intervention.studyId,
          }),
          interventionModuleActions.getInterventionContentItemsSuccess({
            interventionContentItems: interventionModules.concat(interventionInformativePages),
          }),
        ];
      })
    )
  );

  getInterventionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(interventionActions.getInterventionError),
        withLatestFrom(this.store.select(selectSelectedStudy)),
        tap(([{ err }, selectedStudy]) => this.toastrService.error(`Unable to save intervention`))
      ),
    { dispatch: false }
  );

  setStartModule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.setStartModule),
      switchMap(({ studyId, interventionId, moduleId }) => {
        return this.interventionService.setStartModule(studyId, interventionId, moduleId).pipe(
          map((interventionContentItem) => {
            const newInterventionContentItemCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(
              interventionContentItem
            );
            return interventionActions.setStartModuleSuccess({
              interventionContentItem: newInterventionContentItemCamelCase,
            });
          }),
          catchError((err: HttpErrorResponse) => of(interventionActions.setStartModuleError({ err })))
        );
      })
    )
  );

  setStartModuleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionActions.setStartModuleSuccess),
      withLatestFrom(this.store.select(selectSelectedStudy)),
      map(([{ interventionContentItem }, selectedStudy]) => {
        this.toastrService.success('This module is set as a start of The Career Compass');
        return interventionActions.getIntervention({
          studyId: selectedStudy.id,
          interventionId: interventionContentItem.interventionId,
        });
      })
    )
  );

  setStartModuleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(interventionActions.setStartModuleError),
        tap(({ err }) => this.toastrService.error(`Unable to set this module as a start of The Career Compass`))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private interventionService: InterventionService,
    private toastrService: ToastrService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe
  ) {}
}
